import { ChartOptions, TooltipItem } from "chart.js";
import { FC, memo, useMemo } from "react";
import { Doughnut } from "react-chartjs-2";
import styled from "styled-components";

import { useRtl } from "@ryde/redux";
import { IBarGraphContent } from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { useDashboardChartPoints } from "../../hooks/use-dashboard-chart-points";
import { DASHBOARD_CHART_BASE_OPTIONS } from "./dashboard-chart.constants";
import { defaultChartValueFormatter } from "./dashboard-chart.utils";
import { PieChartConstants } from "./dashboard-pie.chart.constants";
import { DashboardPieLegend, IPieLegendItem } from "./dashboard-pie.legend";

interface IDashboardPieChartProps {
  content: IBarGraphContent;
  /** Formats the raw metric value in the tooltip; defaults to a localized number. */
  valueFormatter?: (value: number) => string;
}

const sliceColor = (index: number): string =>
  PieChartConstants.palette[index % PieChartConstants.palette.length];

/**
 * Dashboard pie/donut: each unit + its share live in a custom side legend that
 * folds a long list into a "+N" hover popup, plus the metric on hover. No
 * on-slice numbers — they don't fit or center on thin slices when there are
 * many units (and misbehave in RTL); the legend + tooltip carry every value.
 */
export const DashboardPieChart: FC<IDashboardPieChartProps> = memo(
  ({ content, valueFormatter }) => {
    const { isRtl } = useRtl();
    const { labels, values } = useDashboardChartPoints(content);
    const formatValue = useMemo(
      () => valueFormatter ?? defaultChartValueFormatter,
      [valueFormatter],
    );
    const total = useMemo(
      () => values.reduce((sum, value) => sum + value, 0),
      [values],
    );

    const data = useMemo(
      () => ({
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: values.map((_, index) => sliceColor(index)),
            borderColor: rydeColors.white,
            borderWidth: PieChartConstants.sliceBorderWidth,
          },
        ],
      }),
      [labels, values],
    );

    const legendItems = useMemo<IPieLegendItem[]>(
      () =>
        labels.map((label, index) => ({
          label: String(label),
          color: sliceColor(index),
          share: total > 0 ? Math.round((values[index] / total) * 100) : 0,
        })),
      [labels, values, total],
    );

    const options: ChartOptions<"doughnut"> = useMemo(
      () => ({
        ...DASHBOARD_CHART_BASE_OPTIONS,
        cutout: PieChartConstants.doughnutCutout,
        plugins: {
          // Custom side legend (rendered below) replaces the built-in one.
          legend: { display: false },
          // No on-slice labels — the per-unit share is in the legend + tooltip.
          datalabels: { display: false },
          tooltip: {
            rtl: isRtl,
            callbacks: {
              label: (item: TooltipItem<"doughnut">) => {
                const value = Number(item.raw);
                const share =
                  total > 0 ? ((value / total) * 100).toFixed(0) : "0";
                return `${item.label}: ${formatValue(value)} (${share}%)`;
              },
            },
          },
        },
      }),
      [total, isRtl, formatValue],
    );

    return (
      <PieLayoutStyled $rtl={isRtl}>
        <DashboardPieLegend items={legendItems} isRtl={isRtl} />
        <div className="pie-canvas">
          <Doughnut data={data} options={options} />
        </div>
      </PieLayoutStyled>
    );
  },
);

DashboardPieChart.displayName = "DashboardPieChart";

const PieLayoutStyled = styled.div<{ $rtl: boolean }>`
  display: flex;
  flex-direction: ${({ $rtl }) => ($rtl ? "row" : "row-reverse")};
  align-items: center;
  gap: 12px;
  height: 100%;

  .pie-canvas {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
  }
`;
